import React from 'react';
import styled from 'styled-components';
import AboutTopBar from '../component/about/AboutTopBar';
import AboutTopBarFixed from '../component/about/AboutTopBarFixed';
import MemberInfo from '../component/about/MemberInfo';
import CareerList from '../component/about/CareerList';
import { useShortCut } from '../shared/hooks/useShortCut';
import ArchivePageFrame from '../shared/component/ArchivePageFrame';
import { useNavLayout } from '../shared/hooks/useNavLayout';
import { useAboutPage } from '../shared/hooks/page/useAboutPage';
import { AboutContentMode, DeviceType, NavLayout } from '../shared/enum/EnumRepository';
import HintWrapper from '../shared/component/headless/button/HintWrapper';
import { getHintMessage } from '../shared/constants/hintMessages';
import UniversalIconBtn from '../shared/component/headless/button/UniversalIconBtn';
import downloadIcon from '../asset/icon/download.png';
import reorderIcon from '../asset/icon/reorder.png';
import confirmIcon from '../asset/icon/confirm2.png';
import confirmIcon2 from '../asset/icon/confirm3.png';


const About: React.FC = () => {
  const { handleCmdE } = useShortCut();
  const navLayout = useNavLayout();
  const {
    isEditMode,
    showBothLanguages,
    deviceType,
    canReorderCareerSections,
    hasAboutChanged,
    handleConfirmAll,
    handleDownloadPDF,
    handleReorderCareerSections,
  } = useAboutPage({ load: true });
  const isStacked =
    deviceType === DeviceType.MOBILE || deviceType === DeviceType.TABLET;
  const isMobile = deviceType === DeviceType.MOBILE;
  const showPairedLayout = showBothLanguages && !isStacked;

  return (
    <ArchivePageFrame
      onKeyDown={(e) => {
        handleCmdE(e);
      }}
      tabIndex={-1}
    >
      {navLayout === NavLayout.FIXED ? <AboutTopBarFixed /> : <AboutTopBar />}
      {showPairedLayout ? (
        <PairedAboutContent>
          <MemberInfo contentMode={AboutContentMode.Orig} fullWidth />
          <MemberInfo
            contentMode={AboutContentMode.ENG}
            isPrimary={false}
            englishOnly
            fullWidth
          />
          <PairedCareerList>
            <CareerList
              contentMode={AboutContentMode.Orig}
              secondaryContentMode={AboutContentMode.ENG}
              fullWidth
            />
          </PairedCareerList>
        </PairedAboutContent>
      ) : (
        <AboutContentLayout
          $showBothLanguages={showBothLanguages}
          $isStacked={isStacked}
        >
          <LanguageColumn
            $isMobile={isMobile}
            $isSideBySide={false}
          >
            <MemberInfo
              contentMode={AboutContentMode.Orig}
              fullWidth
            />
            <CareerList
              contentMode={AboutContentMode.Orig}
              fullWidth
            />
          </LanguageColumn>
          {showBothLanguages && (
            <LanguageColumn
              $isMobile={isMobile}
              $isSideBySide={false}
            >
              <MemberInfo
                contentMode={AboutContentMode.ENG}
                isPrimary={false}
                fullWidth
              />
              <CareerList
                contentMode={AboutContentMode.ENG}
                isPrimary={false}
                fullWidth
              />
            </LanguageColumn>
          )}
        </AboutContentLayout>
      )}
      {(isEditMode && deviceType !== DeviceType.MOBILE) &&
        <BtnContainer>
          {hasAboutChanged && (
            <UniversalIconBtn
              isPrimary
              size="standard"
              border={false}
              backgroundImg={confirmIcon}
              backgroundImgHover={confirmIcon2}
              onClick={handleConfirmAll}
            />
          )}
          {canReorderCareerSections && (
            <HintWrapper
              content={getHintMessage('REORDER_CAREER_SECTIONS')}
              toLeft={true}>
              <UniversalIconBtn
                size="standard"
                border={false}
                backgroundImg={reorderIcon}
                onClick={handleReorderCareerSections}
              />
            </HintWrapper>
          )}
          <HintWrapper
            content={getHintMessage('DOWNLOAD_CV_PDF')}
            toLeft={true}>
            <UniversalIconBtn
              size="standard"
              border={false}
              backgroundImg={downloadIcon}
              onClick={handleDownloadPDF}
            />
          </HintWrapper>
        </BtnContainer>
      }
    </ArchivePageFrame>
  );
}

export default About;

const PairedAboutContent = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 600px));
  column-gap: clamp(100px, 8vw, 150px);
  justify-content: center;
  align-items: start;
`;

const PairedCareerList = styled.div`
  grid-column: 1 / -1;
  width: 100%;
`;

const AboutContentLayout = styled.div<{
  $showBothLanguages: boolean;
  $isStacked: boolean;
}>`
  width: 100%;
  display: flex;
  flex-direction: ${({ $isStacked }) => ($isStacked ? 'column' : 'row')};
  align-items: flex-start;
  gap: ${({ $showBothLanguages, $isStacked }) =>
    $showBothLanguages
      ? $isStacked
        ? 'clamp(80px, 10vh, 120px)'
        : 'clamp(24px, 4vw, 60px)'
      : '0'};
`;

const LanguageColumn = styled.section<{
  $isMobile: boolean;
  $isSideBySide: boolean;
}>`
  width: ${({ $isMobile, $isSideBySide }) =>
    $isMobile ? '100%' : $isSideBySide ? '0' : 'clamp(320px, 65vw, 600px)'};
  min-width: 0;
  max-width: ${({ $isSideBySide }) => ($isSideBySide ? '600px' : 'none')};
  flex: ${({ $isSideBySide }) => ($isSideBySide ? '1 1 0' : '0 0 auto')};
`;

const BtnContainer = styled.div`
  position: fixed;
  bottom: clamp(25px, 3vw, 50px);
  right: clamp(25px, 6vw, 75px);
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: clamp(5px, 0.6vw, 15px);
  z-index: 4;
`;
